/**
 * Graphics quality presets for weaker devices (tablets / phones).
 * Persisted in localStorage under GRAPHICS_STORAGE_KEY.
 */

export const GRAPHICS_STORAGE_KEY = 'minelunky.graphics.v1';

export type GraphicsQuality = 'auto' | 'low' | 'medium' | 'high';

export interface ResolvedGraphics {
  /** Effective tier after resolving `auto`. */
  readonly tier: 'low' | 'medium' | 'high';
  /** Cap passed to WebGLRenderer.setPixelRatio. */
  readonly pixelRatioCap: number;
  /** Extra canvas scale (1 = native capped DPR). */
  readonly renderScale: number;
  readonly fogNear: number;
  readonly fogFar: number;
  /** Keep the short player rim light. */
  readonly rimLight: boolean;
  /** Keep PointLights on exit portals. */
  readonly portalLights: boolean;
  /** Player lantern intensity / distance. */
  readonly lampIntensity: number;
  readonly lampDistance: number;
  /** Minimap terrain+overlay refresh Hz. */
  readonly minimapHz: number;
  readonly shadowReceive: boolean;
}

export interface GraphicsSettings {
  quality: GraphicsQuality;
}

const DEFAULT_SETTINGS: GraphicsSettings = Object.freeze({
  quality: 'auto',
});

const TIER_PRESETS: Record<'low' | 'medium' | 'high', ResolvedGraphics> = {
  low: {
    tier: 'low',
    pixelRatioCap: 1,
    renderScale: 0.6,
    fogNear: 10,
    fogFar: 32,
    rimLight: false,
    portalLights: false,
    lampIntensity: 1.6,
    lampDistance: 18,
    minimapHz: 8,
    shadowReceive: false,
  },
  medium: {
    tier: 'medium',
    pixelRatioCap: 1.25,
    renderScale: 0.85,
    fogNear: 14,
    fogFar: 42,
    rimLight: false,
    portalLights: true,
    lampIntensity: 2.0,
    lampDistance: 22,
    minimapHz: 12,
    shadowReceive: false,
  },
  high: {
    tier: 'high',
    pixelRatioCap: 1.5,
    renderScale: 1,
    fogNear: 18,
    fogFar: 52,
    rimLight: true,
    portalLights: true,
    lampIntensity: 2.4,
    lampDistance: 28,
    minimapHz: 20,
    shadowReceive: true,
  },
};

/** Heuristic for cheap devices — touch tablets and low-memory phones. */
export function detectWeakDevice(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const mobileUa = /Android|iPhone|iPad|iPod|Mobile|Tablet|Silk/i.test(ua);
  const touchPoints = navigator.maxTouchPoints || 0;
  const cores = navigator.hardwareConcurrency || 4;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;

  if (mobileUa) return true;
  // iPadOS desktop UA still reports touch + often modest core counts.
  if (touchPoints > 1 && cores <= 6) return true;
  if (typeof memory === 'number' && memory <= 4) return true;
  return false;
}

export function createDefaultGraphicsSettings(): GraphicsSettings {
  return { quality: DEFAULT_SETTINGS.quality };
}

export function loadGraphicsSettings(): GraphicsSettings {
  try {
    const raw = localStorage.getItem(GRAPHICS_STORAGE_KEY);
    if (!raw) return createDefaultGraphicsSettings();
    const parsed = JSON.parse(raw) as Partial<GraphicsSettings>;
    const quality = parseQuality(parsed.quality);
    return { quality };
  } catch {
    return createDefaultGraphicsSettings();
  }
}

export function saveGraphicsSettings(settings: GraphicsSettings): void {
  try {
    localStorage.setItem(GRAPHICS_STORAGE_KEY, JSON.stringify({ quality: settings.quality }));
  } catch {
    // Quota / private mode — ignore.
  }
}

export function resolveGraphics(settings: GraphicsSettings = loadGraphicsSettings()): ResolvedGraphics {
  const tier =
    settings.quality === 'auto'
      ? detectWeakDevice()
        ? 'low'
        : 'high'
      : settings.quality;
  return TIER_PRESETS[tier];
}

export function qualityLabel(quality: GraphicsQuality): string {
  switch (quality) {
    case 'auto':
      return 'Auto';
    case 'low':
      return 'Low';
    case 'medium':
      return 'Medium';
    case 'high':
      return 'High';
  }
}

function parseQuality(value: unknown): GraphicsQuality {
  if (value === 'auto' || value === 'low' || value === 'medium' || value === 'high') {
    return value;
  }
  return 'auto';
}
